import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface College {
  id: string;
  name: string;
  type: 'medical' | 'engineering' | 'arts' | 'nursing' | 'pharmacy' | 'dental' | 'polytechnic' | 'veterinary';
  location: string;
  fees: string;
  seats: number;
  ranking: string;
  accreditation: string;
  established: string;
  status: 'active' | 'inactive';
  description?: string;
  image?: string;
  features?: string[];
  contact?: {
    phone?: string;
    email?: string;
    website?: string;
    address?: string;
  };
}

interface CollegeContextType {
  colleges: College[];
  addCollege: (college: Omit<College, 'id'>) => void;
  updateCollege: (id: string, college: Partial<College>) => void;
  deleteCollege: (id: string) => void;
  getCollegeById: (id: string) => College | undefined;
  getCollegesByType: (type: College['type']) => College[];
}

const CollegeContext = createContext<CollegeContextType | undefined>(undefined);

// Initial colleges data - Empty dataset
const initialColleges: College[] = [];

export const CollegeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [colleges, setColleges] = useState<College[]>(() => {
    // Always start with empty data - ignore localStorage
    localStorage.removeItem('colleges'); // Clear any existing data
    return initialColleges;
  });

  // Save to localStorage whenever colleges change
  React.useEffect(() => {
    // Only save if there are colleges, otherwise keep it empty
    if (colleges.length > 0) {
      localStorage.setItem('colleges', JSON.stringify(colleges));
    } else {
      localStorage.removeItem('colleges'); // Remove completely when empty
    }
  }, [colleges]);

  const addCollege = (collegeData: Omit<College, 'id'>) => {
    const newCollege: College = {
      ...collegeData,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9)
    };
    const updatedColleges = [...colleges, newCollege];
    setColleges(updatedColleges);
  };

  const updateCollege = (id: string, updates: Partial<College>) => {
    setColleges(colleges.map(college => 
      college.id === id ? { ...college, ...updates } : college
    ));
  };

  const deleteCollege = (id: string) => {
    setColleges(colleges.filter(college => college.id !== id));
  };

  const getCollegeById = (id: string): College | undefined => {
    return colleges.find(college => college.id === id);
  };

  const getCollegesByType = (type: College['type']): College[] => {
    return colleges.filter(college => college.type === type);
  };

  const value: CollegeContextType = {
    colleges,
    addCollege,
    updateCollege,
    deleteCollege,
    getCollegeById,
    getCollegesByType
  };

  return (
    <CollegeContext.Provider value={value}>
      {children}
    </CollegeContext.Provider>
  );
};

export const useColleges = () => {
  const context = useContext(CollegeContext);
  if (context === undefined) {
    throw new Error('useColleges must be used within a CollegeProvider');
  }
  return context;
};
